import { FC } from 'react';
import { IChangeEvent, withTheme } from '@rjsf/core';
import { Theme as MuiTheme } from '@rjsf/material-ui';
import validator from '@rjsf/validator-ajv8';
import {
  DefaultArrayFieldTemplate,
  DefaultObjectFieldTemplate,
} from '../fields';
import DefaultFieldTemplate from '../fields/ArrayFieldTemplate/DefaultFieldTemplate';
import * as widgetOverrides from './WidgetOverrides';
import * as templateOverrides from './TemplateOverrides';
import { useDefaultFields } from './useDefaultFields';
import { FieldExtensionOptions } from '../../extensions';

const Form = withTheme(MuiTheme);

export interface ScaffolderFormRendererProps {
  schema: Record<string, any>;
  uiSchema?: Record<string, any>;
  formData: Record<string, any>;
  formContext?: Record<string, any>;
  onChange?: (e: IChangeEvent) => void;
  fields?: FieldExtensionOptions<any, any>[];
  children?: React.ReactNode;
  templates?: Record<string, any>;
}

/**
 * Lightweight form renderer reusing the same RJSF setup (templates, fields,
 * widgets, overrides) as the full scaffolder MultiStepJsonForm, but without
 * stepper logic, route params, or Witty context providers.
 *
 * Use this whenever you need a scaffolder-looking form outside the scaffolder
 * route tree (e.g. the Autofix drawer).
 */
export const ScaffolderFormRenderer: FC<ScaffolderFormRendererProps> = ({
  schema,
  uiSchema = {},
  formData,
  formContext,
  onChange,
  fields: extraFields,
  children,
  templates: extraTemplates,
}) => {
  const usedFields = useDefaultFields(extraFields);

  return (
    <Form
      schema={schema}
      uiSchema={uiSchema}
      validator={validator}
      formData={formData}
      formContext={formContext}
      onChange={onChange}
      fields={usedFields}
      widgets={{ ...widgetOverrides }}
      templates={{
        ArrayFieldTemplate: DefaultArrayFieldTemplate,
        ObjectFieldTemplate: DefaultObjectFieldTemplate,
        FieldTemplate: DefaultFieldTemplate,
        ...templateOverrides,
        ...extraTemplates,
      }}
      noHtml5Validate
      noValidate
      liveValidate={false}
    >
      {children ?? <></>}
    </Form>
  );
};
